/**
 * LoadingScreen - Displayed while game assets are loading
 * Shows a progress bar and loading text. Transitions to title screen when complete.
 */

import { AssetLoader } from '../utils/AssetLoader.js';

export class LoadingScreen {
  constructor(game) {
    this.game = game;
    this.assetLoader = new AssetLoader();
    this.loadingStarted = false;
    this.transitionTimer = 0;
    this.readyToTransition = false;
  }

  enter(data = {}) {
    this.loadingStarted = false;
    this.readyToTransition = false;
    this.transitionTimer = 0;
  }

  exit() {}

  update(dt) {
    // Start loading on first update (after canvas is ready)
    if (!this.loadingStarted) {
      this.loadingStarted = true;
      this._startLoading();
    }

    // After loading complete, wait a brief moment then transition
    if (this.readyToTransition) {
      this.transitionTimer += dt;
      if (this.transitionTimer > 0.5) {
        console.log('Assets loaded - transitioning to title screen.');
        this.game.assetsLoaded = true;
        this.game.state.setState('title');
      }
    }
  }

  async _startLoading() {
    try {
      await this.assetLoader.loadAll(this.game.audio);
      this.readyToTransition = true;
    } catch (e) {
      console.error('Asset loading failed:', e);
      // Still transition to title - game should be playable even if some assets fail
      this.readyToTransition = true;
    }
  }

  render(ctx) {
    const w = this.game.width;
    const h = this.game.height;

    // Dark background
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, w, h);

    // Loading text - use basic font first (game font might not be loaded yet)
    const progress = this.assetLoader.getProgress();
    const progressPct = Math.round(progress * 100);

    // Title
    ctx.save();
    ctx.font = `${Math.max(24, w * 0.03)}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 10;
    ctx.fillText('MISSILE COMMAND', w / 2, h * 0.35);
    ctx.restore();

    // "Loading..." text
    ctx.save();
    ctx.font = `${Math.max(16, w * 0.02)}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#99aabb';
    ctx.fillText(this.readyToTransition ? 'READY' : 'LOADING...', w / 2, h * 0.48);
    ctx.restore();

    // Progress bar background
    const barWidth = Math.min(400, w * 0.5);
    const barHeight = 8;
    const barX = (w - barWidth) / 2;
    const barY = h * 0.55;

    ctx.fillStyle = '#1a1a3e';
    ctx.fillRect(barX, barY, barWidth, barHeight);

    // Progress bar fill
    const fillWidth = barWidth * progress;
    ctx.fillStyle = '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 8;
    ctx.fillRect(barX, barY, fillWidth, barHeight);
    ctx.shadowBlur = 0;

    // Progress percentage
    ctx.save();
    ctx.font = `${Math.max(12, w * 0.015)}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#8899aa';
    ctx.fillText(`${progressPct}%`, w / 2, barY + barHeight + 20);
    ctx.restore();
  }
}
